// Comprobante de Liquidación Laboral en PDF — Grupo Vértice CR
// Mantiene la misma identidad visual del "Comprobante de Pago" (boletaPDF.js):
// tipografía DejaVu Sans (para el símbolo ₡), paleta neutra gris-tierra,
// tablas con jspdf-autotable y espacios de firma al pie.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DEJAVU_REGULAR_BASE64, DEJAVU_BOLD_BASE64 } from "@/lib/boletaFont";

const FONT_FAMILY = "NomyProSans";

function registerNomyFont(doc) {
  doc.addFileToVFS("NomyProSans-Regular.ttf", DEJAVU_REGULAR_BASE64);
  doc.addFont("NomyProSans-Regular.ttf", FONT_FAMILY, "normal");
  doc.addFileToVFS("NomyProSans-Bold.ttf", DEJAVU_BOLD_BASE64);
  doc.addFont("NomyProSans-Bold.ttf", FONT_FAMILY, "bold");
}

// Paleta neutra (idéntica a boletaPDF.js)
const COLOR_TITLE_BG = [60, 70, 65];
const COLOR_TITLE_FG = [255, 255, 255];
const COLOR_LABEL_BG = [232, 230, 225];
const COLOR_VALUE_BG = [248, 247, 244];
const COLOR_HEADER_BG = [225, 225, 220];
const COLOR_TEXT = [40, 40, 40];
const COLOR_MUTED = [120, 120, 120];
const COLOR_BORDER = [180, 180, 180];
const COLOR_ACCENT = [80, 100, 90];
const COLOR_WARN_BG = [252, 228, 214];
const COLOR_OK_BG = [232, 245, 238];
const COLOR_OK_BORDER = [46, 125, 50];
const COLOR_ERR_BG = [253, 237, 237];
const COLOR_ERR_BORDER = [198, 40, 40];

const fmtCRC = (n) =>
  "₡" +
  new Intl.NumberFormat("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    Number(n) || 0
  );
const fmtFecha = (iso) =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString("es-CR") : "—";

function detectImageFormat(dataUrl) {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  const match = dataUrl.match(/^data:image\/(png|jpeg|jpg|webp);base64,/i);
  if (!match) return null;
  const fmt = match[1].toLowerCase();
  return fmt === "jpg" ? "JPEG" : fmt.toUpperCase();
}

export async function generarLiquidacionPDF({
  empresa,
  empleado,
  fechaSalida,
  motivo,
  calc,
  salarios,
  diasPreavisoUsados,
  diasCesantiaUsados,
  elaboradoPor = "",
  fechaDocumento,
  fileName,
  returnBase64 = true,
  ofertaPatrono = 0,
  skipDownload = false,
}) {
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  registerNomyFont(doc);
  doc.setFont(FONT_FAMILY, "normal");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 10;
  let y = margin;

  // === CABECERA EMPRESA ===
  doc.setFillColor(...COLOR_TITLE_BG);
  doc.rect(margin, y, pageW - margin * 2, 12, "F");
  doc.setTextColor(...COLOR_TITLE_FG);
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(15);
  const empresaNombre = empresa?.razon_social || empresa?.nombre || "Empresa";
  doc.text(empresaNombre, pageW / 2, y + 8, { align: "center" });
  y += 12;

  // === LOGO ===
  const logoFmt = detectImageFormat(empresa?.logo);
  if (logoFmt) {
    try {
      doc.addImage(empresa.logo, logoFmt, pageW - margin - 30, y + 2, 28, 28);
    } catch (e) {
      /* logo invalid, ignore */
    }
  }

  // === Título ===
  y += 4;
  doc.setTextColor(...COLOR_TEXT);
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(12);
  doc.text("COMPROBANTE DE LIQUIDACIÓN LABORAL", margin, y + 4);
  y += 7;
  doc.setFont(FONT_FAMILY, "normal");
  doc.setFontSize(9);
  doc.text("FECHA DE SALIDA:", margin, y + 4);
  doc.setFillColor(...COLOR_LABEL_BG);
  doc.rect(margin + 40, y, 60, 6, "F");
  doc.setFont(FONT_FAMILY, "bold");
  doc.text(fmtFecha(fechaSalida), margin + 40 + 30, y + 4, { align: "center" });
  y += 9;

  // === DATOS DEL TRABAJADOR (izq) + MOTIVO/TIEMPO (der) ===
  const colLW = 100; // ancho bloque izquierdo
  const blockTop = y;
  const rowH = 6.5;

  const empleadoRows = [
    ["Trabajador:", `${empleado?.nombre || ""} ${empleado?.apellidos || ""}`.trim() || "—"],
    ["Cédula:", empleado?.cedula || "—"],
    ["Correo:", empleado?.correo || empleado?.email || "—"],
    ["Puesto:", empleado?.puesto || "—"],
    ["Fecha de Ingreso:", fmtFecha(empleado?.fecha_ingreso)],
    ["Tiempo Laborado:", `${calc.tiempo.anios} año${calc.tiempo.anios === 1 ? "" : "s"}, ${calc.tiempo.mesesRestantes} mes${calc.tiempo.mesesRestantes === 1 ? "" : "es"}`],
  ];

  const drawFittedText = (txt, x, yy, maxW, sizeStart = 8, sizeMin = 6) => {
    let sz = sizeStart;
    let str = String(txt);
    doc.setFontSize(sz);
    while (doc.getTextWidth(str) > maxW && sz > sizeMin) {
      sz -= 0.5;
      doc.setFontSize(sz);
    }
    if (doc.getTextWidth(str) > maxW) {
      while (str.length > 1 && doc.getTextWidth(str + "…") > maxW) str = str.slice(0, -1);
      str = str + "…";
    }
    doc.text(str, x, yy);
    doc.setFontSize(8);
  };

  empleadoRows.forEach((r, i) => {
    const yy = blockTop + i * rowH;
    doc.setFillColor(...COLOR_LABEL_BG);
    doc.rect(margin, yy, 42, rowH, "F");
    doc.setDrawColor(...COLOR_BORDER);
    doc.rect(margin, yy, 42, rowH);
    doc.setFont(FONT_FAMILY, "bold");
    doc.setFontSize(8);
    doc.text(r[0], margin + 1, yy + 4);
    doc.setFillColor(...COLOR_VALUE_BG);
    doc.rect(margin + 42, yy, colLW - 42, rowH, "F");
    doc.rect(margin + 42, yy, colLW - 42, rowH);
    doc.setFont(FONT_FAMILY, "normal");
    drawFittedText(r[1] || "—", margin + 43, yy + 4, colLW - 42 - 2);
  });

  // Bloque derecho: Motivo + días
  const rightX = margin + colLW + 4;
  const rightW = pageW - rightX - 33 - margin; // dejar 33mm para logo
  const rightRows = [
    ["Motivo", motivo?.label || "—"],
    ["Artículo", motivo?.articulo || "—"],
    ["Días Preaviso", `${diasPreavisoUsados || 0} días`],
    ["Días Cesantía", `${diasCesantiaUsados || 0} días`],
    ["Días Vacaciones", `${calc.diasVacacionesPendientes || 0} días`],
    ["Elaborado por", elaboradoPor || "—"],
  ];
  autoTable(doc, {
    startY: blockTop,
    margin: { left: rightX },
    tableWidth: rightW,
    head: [["Concepto", "Detalle"]],
    body: rightRows,
    theme: "grid",
    styles: {
      font: FONT_FAMILY,
      fontSize: 8,
      cellPadding: 1.2,
      textColor: COLOR_TEXT,
      lineColor: COLOR_BORDER,
      lineWidth: 0.1,
    },
    headStyles: { fillColor: COLOR_HEADER_BG, textColor: COLOR_TEXT, fontStyle: "bold", halign: "center" },
    columnStyles: {
      0: { halign: "left", fillColor: COLOR_LABEL_BG, fontStyle: "bold", cellWidth: 34 },
      1: { halign: "left", fillColor: COLOR_VALUE_BG },
    },
  });

  y = Math.max(blockTop + empleadoRows.length * rowH, doc.lastAutoTable.finalY) + 4;

  // === DESGLOSE DE SALARIOS (izq) + CÁLCULOS (der) ===
  const halfW = (pageW - margin * 2) / 2 - 2;

  // Izquierda: Salarios mensuales
  const salariosBody = (salarios || []).map((s) => [
    String(s.year || ""),
    s.label || "",
    fmtCRC(s.salarios || 0),
    s.especie ? fmtCRC(s.especie) : "—",
  ]);
  const totalSalarios = (salarios || []).reduce(
    (a, s) => a + Number(s.salarios || 0) + Number(s.especie || 0),
    0
  );

  autoTable(doc, {
    startY: y,
    margin: { left: margin },
    tableWidth: halfW,
    head: [
      [{ content: "Desglose de Salarios (últimos 12 meses)", colSpan: 4, styles: { halign: "center" } }],
      ["Año", "Mes", "Salario Bruto", "En Especie"],
    ],
    body: [
      ...salariosBody,
      [
        { content: "Total:", colSpan: 2, styles: { halign: "right", fontStyle: "bold", fillColor: COLOR_LABEL_BG } },
        { content: fmtCRC(totalSalarios), colSpan: 2, styles: { halign: "right", fontStyle: "bold", fillColor: COLOR_ACCENT, textColor: [255, 255, 255] } },
      ],
    ],
    theme: "grid",
    styles: {
      font: FONT_FAMILY,
      fontSize: 7.5,
      cellPadding: 1.2,
      textColor: COLOR_TEXT,
      lineColor: COLOR_BORDER,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: COLOR_HEADER_BG,
      textColor: COLOR_TEXT,
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 14 },
      1: { halign: "left" },
      2: { halign: "right" },
      3: { halign: "right" },
    },
  });
  const yLeftAfter = doc.lastAutoTable.finalY;

  // Derecha: Cálculos (promedios)
  const calculosBody = [
    ["Salario Promedio 6 meses", fmtCRC(calc.prom6)],
    ["Salario Diario Prom. 6 meses", fmtCRC(calc.promDiario6)],
    ["Salario Promedio 50 Semanas", fmtCRC(calc.prom50)],
    ["Salario Diario Prom. 50 Semanas", fmtCRC(calc.promDiario50)],
  ];
  autoTable(doc, {
    startY: y,
    margin: { left: pageW / 2 + 1 },
    tableWidth: halfW,
    head: [["Cálculos (Art. 30 y 157 CT)", "Monto"]],
    body: calculosBody,
    theme: "grid",
    styles: {
      font: FONT_FAMILY,
      fontSize: 8,
      cellPadding: 1.5,
      textColor: COLOR_TEXT,
      lineColor: COLOR_BORDER,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: COLOR_HEADER_BG,
      textColor: COLOR_TEXT,
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: {
      0: { halign: "left", fillColor: COLOR_LABEL_BG, fontStyle: "bold" },
      1: { halign: "right", fillColor: COLOR_VALUE_BG },
    },
  });
  const yCalcAfter = doc.lastAutoTable.finalY;

  // Rubros de liquidación
  const check = (v) => (v ? "Sí" : "No");
  const rubrosBody = [
    ["Aguinaldo", check(calc.incluir.aguinaldo), fmtCRC(calc.montos.aguinaldo)],
    ["Vacaciones", check(calc.incluir.vacaciones), fmtCRC(calc.montos.vacaciones)],
    ["Preaviso", check(calc.incluir.preaviso), fmtCRC(calc.montos.preaviso)],
    ["Cesantía", check(calc.incluir.cesantia), fmtCRC(calc.montos.cesantia)],
  ];
  autoTable(doc, {
    startY: yCalcAfter + 2,
    margin: { left: pageW / 2 + 1 },
    tableWidth: halfW,
    head: [["Rubros", "Incl.", "Monto"]],
    body: rubrosBody,
    theme: "grid",
    styles: {
      font: FONT_FAMILY,
      fontSize: 8,
      cellPadding: 1.5,
      textColor: COLOR_TEXT,
      lineColor: COLOR_BORDER,
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: COLOR_HEADER_BG,
      textColor: COLOR_TEXT,
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: {
      0: { halign: "left", fillColor: COLOR_LABEL_BG, fontStyle: "bold" },
      1: { halign: "center", cellWidth: 12, fontStyle: "bold" },
      2: { halign: "right", fillColor: COLOR_VALUE_BG },
    },
    didParseCell: (data) => {
      // Colorear Sí/No
      if (data.section === "body" && data.column.index === 1) {
        const isCheck = data.cell.raw === "Sí";
        data.cell.styles.textColor = isCheck ? [46, 125, 50] : [198, 40, 40];
      }
    },
  });
  const yRubrosAfter = doc.lastAutoTable.finalY;

  // Total de liquidación + diferencia
  autoTable(doc, {
    startY: yRubrosAfter + 2,
    margin: { left: pageW / 2 + 1 },
    tableWidth: halfW,
    body: [
      ["TOTAL DE LIQUIDACIÓN", fmtCRC(calc.totalLiquidacion)],
      ["Adelanto pagado", fmtCRC(calc.adelanto)],
      ["DIFERENCIA A PAGAR", fmtCRC(calc.diferencia)],
    ],
    theme: "grid",
    styles: {
      font: FONT_FAMILY,
      fontSize: 9,
      cellPadding: 2,
      textColor: COLOR_TEXT,
      lineColor: COLOR_BORDER,
      lineWidth: 0.2,
    },
    columnStyles: {
      0: { halign: "left", fontStyle: "bold" },
      1: { halign: "right", fontStyle: "bold" },
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === 0) {
        data.cell.styles.fillColor = COLOR_ACCENT;
        data.cell.styles.textColor = [255, 255, 255];
      }
      if (data.section === "body" && data.row.index === 2) {
        data.cell.styles.fillColor = COLOR_WARN_BG;
      }
    },
  });
  const yTotalAfter = doc.lastAutoTable.finalY;

  y = Math.max(yLeftAfter, yTotalAfter) + 4;

  // === Tarifas contractuales (fila completa) ===
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["Salario Diario Contractual", "Hora Ordinaria", "Hora Mixta", "Hora Nocturna"]],
    body: [[
      fmtCRC(calc.salarioDiarioContractual),
      fmtCRC(calc.horaOrdinaria),
      fmtCRC(calc.horaMixta),
      fmtCRC(calc.horaNocturna),
    ]],
    theme: "grid",
    styles: {
      font: FONT_FAMILY,
      fontSize: 9,
      cellPadding: 2,
      textColor: COLOR_TEXT,
      lineColor: COLOR_BORDER,
      lineWidth: 0.1,
      halign: "center",
    },
    headStyles: { fillColor: COLOR_ACCENT, textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
    bodyStyles: { fillColor: COLOR_VALUE_BG, fontStyle: "bold", halign: "center" },
  });
  y = doc.lastAutoTable.finalY + 4;

  // === Oferta del patrono (opcional) ===
  if (ofertaPatrono > 0) {
    const dif = Number(ofertaPatrono) - calc.totalLiquidacion;
    const cumple = dif >= 0;
    const bg = cumple ? COLOR_OK_BG : COLOR_ERR_BG;
    const brd = cumple ? COLOR_OK_BORDER : COLOR_ERR_BORDER;
    const title = cumple
      ? "La oferta del patrono CUMPLE con el mínimo legal"
      : "ADVERTENCIA — La oferta del patrono está POR DEBAJO del mínimo legal";
    const detail = cumple
      ? `Excedente a favor del trabajador: ${fmtCRC(dif)}`
      : `Faltante por pagar: ${fmtCRC(Math.abs(dif))}`;
    const boxH = 16;
    if (y + boxH > pageH - 40) { doc.addPage(); y = margin; }
    doc.setFillColor(...bg);
    doc.setDrawColor(...brd);
    doc.setLineWidth(0.4);
    doc.rect(margin, y, pageW - margin * 2, boxH, "FD");
    doc.setTextColor(...brd);
    doc.setFont(FONT_FAMILY, "bold");
    doc.setFontSize(9);
    doc.text(title, margin + 3, y + 5.5);
    doc.setFont(FONT_FAMILY, "normal");
    doc.setFontSize(8);
    doc.setTextColor(...COLOR_TEXT);
    doc.text(
      `Mínimo legal: ${fmtCRC(calc.totalLiquidacion)}   ·   Oferta del patrono: ${fmtCRC(ofertaPatrono)}`,
      margin + 3, y + 10
    );
    doc.setFont(FONT_FAMILY, "bold");
    doc.setTextColor(...brd);
    doc.text(detail, margin + 3, y + 14);
    y += boxH + 4;
  }

  // === Nota legal ===
  if (y + 14 > pageH - 40) { doc.addPage(); y = margin; }
  const notaTxt =
    `Cálculo conforme al Código de Trabajo de Costa Rica — Art. 28 (preaviso), Art. 29 (cesantía con tope 8 años / 176 días), Art. 153/157 (vacaciones) y Ley N° 2412 (aguinaldo). Motivo: ${motivo?.label || "—"} (${motivo?.articulo || "—"}). Documento emitido el ${fmtFecha(fechaDocumento)}.`;
  doc.setFillColor(248, 244, 234);
  doc.setDrawColor(180, 160, 120);
  doc.setLineWidth(0.2);
  const lineas = doc.splitTextToSize(notaTxt, pageW - margin * 2 - 4);
  const altura = Math.max(10, 3 + lineas.length * 3.2);
  doc.rect(margin, y, pageW - margin * 2, altura, "FD");
  doc.setTextColor(120, 90, 30);
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(7.5);
  doc.text("Nota legal:", margin + 2, y + 4);
  doc.setTextColor(...COLOR_TEXT);
  doc.setFont(FONT_FAMILY, "normal");
  doc.setFontSize(7);
  lineas.forEach((line, i) => doc.text(line, margin + 18, y + 4 + i * 3.2));
  y = y + altura + 2;

  // === FIRMAS al pie (Trabajador + Patrono) ===
  let firmasY = Math.max(y + 22, pageH - 34);
  if (firmasY + 22 > pageH - margin) { doc.addPage(); firmasY = pageH - 40; }
  doc.setDrawColor(...COLOR_BORDER);
  doc.setLineWidth(0.3);
  const firmaW = 78;
  const firma1X = pageW * 0.25 - firmaW / 2;
  const firma2X = pageW * 0.75 - firmaW / 2;
  doc.line(firma1X, firmasY, firma1X + firmaW, firmasY);
  doc.line(firma2X, firmasY, firma2X + firmaW, firmasY);

  const trabajadorNombre =
    `${empleado?.nombre || ""} ${empleado?.apellidos || ""}`.trim() || "—";
  const cedulaTrab = empleado?.cedula ? `Cédula: ${empleado.cedula}` : "";
  const patronoNombre = empresa?.razon_social || empresa?.nombre || "—";
  const cedulaPatr = empresa?.cedula_juridica ? `Cédula jurídica: ${empresa.cedula_juridica}` : "";

  doc.setTextColor(...COLOR_TEXT);
  doc.setFont(FONT_FAMILY, "bold");
  doc.setFontSize(9);
  doc.text(trabajadorNombre, pageW * 0.25, firmasY + 4.5, { align: "center" });
  doc.text(patronoNombre, pageW * 0.75, firmasY + 4.5, { align: "center" });

  doc.setFont(FONT_FAMILY, "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...COLOR_MUTED);
  doc.text("Firma del Trabajador", pageW * 0.25, firmasY + 8.5, { align: "center" });
  doc.text("Firma por el Patrono", pageW * 0.75, firmasY + 8.5, { align: "center" });

  if (cedulaTrab) doc.text(cedulaTrab, pageW * 0.25, firmasY + 12, { align: "center" });
  if (cedulaPatr) doc.text(cedulaPatr, pageW * 0.75, firmasY + 12, { align: "center" });

  // Pie de página
  doc.setFont(FONT_FAMILY, "normal");
  doc.setFontSize(7);
  doc.setTextColor(...COLOR_MUTED);
  const fechaGen = new Date().toLocaleDateString("es-CR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  doc.text(`Generado el ${fechaGen} · ${empresa?.razon_social || empresa?.nombre || elaboradoPor || ""}`, pageW - margin, pageH - 5, { align: "right" });

  // Guardar
  const safeName = `${empleado?.nombre || ""}_${empleado?.apellidos || ""}`
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9_\-]+/g, "_");
  const finalFileName =
    fileName || `Liquidacion_${safeName}_${(fechaSalida || "").slice(0, 10)}.pdf`;
  if (!skipDownload) doc.save(finalFileName);

  const result = { fileName: finalFileName };
  if (returnBase64) {
    const pdfDataUri = doc.output("datauristring");
    result.pdfBase64 = pdfDataUri.split(",")[1];
  }
  return result;
}
